import type { ComponentProps } from "react";

import type { OverlapOut, ResultCaptureGapOut, SegmentOut } from "../contract/api";
import { t } from "../contract/strings";
import { durationWords } from "../engine/duration-words";
import { cx } from "./cx";
import { MicIcon, WarningIcon } from "./icons";
import { speakerName } from "./speaker-name";

/**
 * One speaker's turn, in the server's own wire shape (`contract/openapi.json`).
 *
 * `source` says where the attribution came from: `residual` is the server's clustering
 * rather than a device's own claim, and is drawn apart -- dashed rail, unbolded name,
 * quieter -- because it is a weaker statement about who spoke, not a different kind of
 * speech. `contested_with` names the people whose own device claimed part of this stretch
 * and whose copy is not being shown, so a reader deciding whether to trust a line sees the
 * disagreement where it happened rather than three screens away in a list of codes.
 * `timing` is how far the placement can be trusted; absent on older results, which reads
 * as unknown and draws nothing.
 */
export type Segment = SegmentOut;

export type Overlap = OverlapOut;

/**
 * A stretch (5 s or more) a device attested its microphone was suspended, placed on
 * the session clock (#465): the server's own `ResultCaptureGapOut`.
 */
export type CaptureGap = ResultCaptureGapOut;

export interface TimelineProps extends Omit<ComponentProps<"div">, "children"> {
  segments?: Segment[];
  overlaps?: Overlap[];
  /**
   * Attested stretches a device was out of the meeting. Drawn on the ladder at the
   * stretch's start, so the note sits where the words are missing, not in a list of
   * codes three screens away.
   */
  captureGaps?: CaptureGap[];
  /**
   * Speakers in the order they should take the theme colours -- the person cards'
   * order, so Sarah is teal on her card *and* on every turn she speaks below. Left
   * undefined the map falls back to first appearance in the transcript.
   */
  speakerOrder?: string[];
  /** A pre-merged, chronologically paged event list from the result screen. */
  entries?: TimelineEntry[];
  /**
   * Split a turn's words into plain and matching runs; the matching ones are marked. The
   * result screen's search passes its own, so the timeline knows nothing of queries.
   */
  highlight?: (text: string) => Array<{ text: string; match: boolean }>;
  /** The start of the turn to draw as the one just jumped to, or `null`. */
  focusStart?: number | null;
}

/** m:ss. Sessions are minutes long, so hours would be a column of zeroes. */
export function fmtClock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function fmtClockPad(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * A capture gap's length as a person reads it: "40 seconds", "1 minute 5 seconds".
 * Seconds stay visible past a minute because the note marks a stretch, and "1 minute"
 * would hide whether it was a 61 s blip or a 119 s absence. In words, as the note is a
 * sentence; the core's `gap_duration_message`, which the apps render, picks the same
 * keys.
 */
export function fmtGapDuration(seconds: number): string {
  return durationWords(Math.max(1, Math.round(seconds)));
}

/**
 * A short span of speech to the tenth of a second, in the shared copy: "19.7 s". A sum
 * of take lengths is a float, and printed as-is it read `19.685000000000002s` (#616).
 */
export function fmtSpeechSeconds(seconds: number): string {
  return t("duration.seconds", { seconds: seconds.toFixed(1) });
}

/**
 * The note a capture gap draws on the ladder, in the reader's own words.
 *
 * The shared fixture's capture-gap rows carry this same message key and
 * placeholders; the web test compares this real sentence to it (`#493`).
 */
export function captureGapNote(gap: CaptureGap): string {
  const duration = fmtGapDuration(gap.end - gap.start);
  return gap.speaker
    ? t("result.capture_gap_named", {
        name: speakerName(gap.speaker),
        duration,
      })
    : t("result.capture_gap_anonymous", { duration });
}

/** The rung that names people talking over each other, as a person reads it. */
export function overlapLabel(overlap: Overlap): string {
  return t("result.overlap", { names: overlap.speakers.map(speakerName).join(" & ") });
}

/**
 * How a turn is drawn: an anonymous voice, residual clustering, and whether it
 * falls under a measured overlap window.
 */
export function segmentFlags(
  segment: Segment,
  overlaps: Overlap[]
): { anonymous: boolean; residual: boolean; overlap: boolean } {
  const anonymous = segment.source === "anonymous";
  const residual = segment.source === "residual" || anonymous;
  const overlap = overlaps.some(
    (ov) => Math.max(segment.start, ov.start) < Math.min(segment.end, ov.end)
  );
  return { anonymous, residual, overlap };
}

export type TimelineEntry =
  | { kind: "segment"; start: number; data: Segment }
  | { kind: "overlap"; start: number; data: Overlap }
  | { kind: "captureGap"; start: number; data: CaptureGap };

/** Merge every timeline event before paging so later pages never reorder earlier events. */
export function timelineEntries(
  segments: Segment[],
  overlaps: Overlap[] = [],
  captureGaps: CaptureGap[] = [],
): TimelineEntry[] {
  return [
    ...segments.map((data) => ({ kind: "segment" as const, start: data.start, data })),
    ...overlaps.map((data) => ({ kind: "overlap" as const, start: data.start, data })),
    ...captureGaps.map((data) => ({ kind: "captureGap" as const, start: data.start, data })),
  ]
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => a.entry.start - b.entry.start || a.index - b.index)
    .map(({ entry }) => entry);
}

const SPEAKER_THEMES = [
  { border: "border-accent", text: "text-accent" },
  { border: "border-secondary", text: "text-secondary" },
  { border: "border-tertiary", text: "text-tertiary" },
];

/**
 * Who spoke when, as one continuous ladder of time.
 *
 * A rail runs down the timestamp column -- the line that makes a stack of turns read as
 * time flowing -- and each turn hangs off it as a note card: monospace timestamp, the
 * speaker's name in their colour, then their words in the body face. A stretch of people
 * talking over each other is drawn between the turns it belongs to, as its own rung.
 *
 * Colour says whose turn it is; the dashed rail and quiet name say the attribution is
 * residual clustering rather than a device's own claim -- a weaker statement about who
 * spoke, drawn quieter but never as an error.
 */
export function Timeline({
  segments = [],
  overlaps = [],
  captureGaps = [],
  speakerOrder,
  entries: suppliedEntries,
  highlight,
  focusStart = null,
  className,
  ...rest
}: TimelineProps) {
  // Map distinct speakers to theme indices
  const speakerIndexMap = new Map<string, number>();
  let speakerCounter = 0;
  const claim = (speaker: string) => {
    if (!speakerIndexMap.has(speaker)) {
      speakerIndexMap.set(speaker, speakerCounter++ % SPEAKER_THEMES.length);
    }
  };
  const entries = suppliedEntries ?? timelineEntries(segments, overlaps, captureGaps);
  const renderedOverlaps =
    overlaps.length > 0
      ? overlaps
      : entries
          .filter(
            (entry): entry is Extract<TimelineEntry, { kind: "overlap" }> =>
              entry.kind === "overlap",
          )
          .map((entry) => entry.data);
  for (const speaker of speakerOrder ?? []) claim(speaker);
  for (const entry of entries) {
    if (entry.kind === "segment") claim(entry.data.speaker);
  }

  return (
    <div className={cx("flex flex-col gap-6 relative", className)} {...rest}>
      {/* The continuous rail, down the middle of the gap between the timestamp column
          and the turns: clear of the digits it would strike through, close enough that
          each card reads as hanging off it. Cards sit above it -- z-10 -- so the ladder
          reads through them, not over them. */}
      <div className="absolute left-14 top-2 bottom-2 w-px bg-line" aria-hidden="true" />

      {entries.map((entry, i) => {
        if (entry.kind === "overlap") {
          const ov = entry.data;
          return (
            <div className="flex gap-4 relative z-10 -my-2 items-center" key={`ov-${ov.start}-${i}`}>
              <div className="w-12 flex-shrink-0" />
              <div className="flex-1 bg-tertiary-container/10 border border-tertiary/30 rounded-control px-2 py-1 flex items-center gap-2">
                <WarningIcon className="size-icon-sm text-tertiary flex-none" />
                {/* The names in their own case, as on the turns. */}
                <span className="font-code text-label font-semibold text-tertiary">
                  {overlapLabel(ov)}
                </span>
              </div>
            </div>
          );
        }

        if (entry.kind === "captureGap") {
          const gap = entry.data;
          const note = captureGapNote(gap);
          return (
            <div className="flex gap-4 relative z-10 -my-2 items-center" key={`gap-${gap.start}-${i}`}>
              <div className="w-12 pt-1 flex-shrink-0 text-right">
                <span className="block font-code text-label font-semibold tracking-label text-ink-soft tabular-nums">
                  {fmtClockPad(gap.start)}
                </span>
              </div>
              <div className="flex-1 bg-surface-2 border border-line rounded-control px-2 py-1 flex items-center gap-2">
                <MicIcon className="size-icon-sm text-ink-soft flex-none" />
                <span className="text-note text-ink-soft">{note}</span>
              </div>
            </div>
          );
        }

        const seg = entry.data;
        // An anonymous voice (`Speaker A`...) is drawn like residual clustering -- a voice
        // no device's owner claimed -- and its name set in italics, so it never reads as a
        // person on the roster.
        const { anonymous, residual, overlap: hasOverlap } = segmentFlags(seg, renderedOverlaps);
        const theme = SPEAKER_THEMES[speakerIndexMap.get(seg.speaker) ?? 0];
        // The turn a "jump to this moment" landed on: tinted for a moment, in the accent
        // family, so the eye finds it after the scroll.
        const focused = focusStart !== null && seg.start === focusStart;

        return (
          <div
            // Clear of the result screen's sticky details card and tab bar when a jump
            // scrolls a turn into view.
            className="flex gap-4 relative z-10 scroll-mt-36"
            key={`seg-${seg.start}-${i}`}
            data-turn-start={seg.start}
            data-focused={focused ? "true" : undefined}
          >
            {/* Timestamp: fixed column, right-aligned, padded, mono -- the register the
                whole ladder's time axis is set in. */}
            <div className="w-12 pt-1 flex-shrink-0 text-right">
              <span className="block font-code text-label font-semibold tracking-label text-ink-soft tabular-nums">
                {fmtClockPad(seg.start)}
              </span>
            </div>

            {/* The turn: a note card hanging off the rail, pointed at it by the one
                corner left square. The speaker's colour is its left edge. */}
            <div
              className={cx(
                "flex-1 min-w-0 p-3 rounded-tr-card rounded-br-card rounded-bl-card border-l-2",
                focused ? "bg-accent-soft transition-colors" : "bg-surface",
                residual ? "border-line border-dashed" : theme.border,
                hasOverlap && "relative overflow-hidden"
              )}
            >
              {/* Speech under an overlap window carries a tinted spine of its own --
                  the same amber family as the marker above it, at reading strength. */}
              {hasOverlap && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-tertiary/20" aria-hidden="true" />
              )}
              <span
                className={cx(
                  // The speaker's name in its own case (#766), not caps.
                  "block mb-1 font-code text-label font-semibold",
                  residual ? "text-ink-soft" : theme.text,
                  anonymous && "italic"
                )}
              >
                {speakerName(seg.speaker)}
              </span>
              {seg.text ? (
                <p
                  className={cx(
                    "m-0 font-sans text-note leading-normal text-ink",
                    residual && "text-ink-soft"
                  )}
                >
                  {highlight
                    ? highlight(seg.text).map((part, k) =>
                        part.match ? (
                          <mark key={k} className="bg-accent-soft text-ink rounded-control px-hair">
                            {part.text}
                          </mark>
                        ) : (
                          part.text
                        )
                      )
                    : seg.text}
                </p>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
